import { useRef, type KeyboardEvent, type ReactNode } from "react";

import { alternadorEmPilulaClass, opcaoDoAlternadorClass } from "../../lib/superficies";
import { cn } from "../../lib/utils";

/**
 * A navegação por seção de dentro de um painel (TUT-158).
 *
 * ## Por que dentro do painel, e não entre painéis
 *
 * A restrição é do brief e não é estética: os ritos de e2e rodam `axe` em cada
 * painel da aba Configurações, e esconder painel inteiro faria a auditoria
 * aprovar uma tela que ninguém vê. A navegação por seção existe para cortar a
 * altura DE UM painel — as configurações agrupadas, as seis regras de alerta —,
 * nunca para sumir com um painel do DOM.
 *
 * ## O que muda em relação ao que existia
 *
 * A TUT-157 já tinha posto abas nas Configurações, e elas eram **meia aba**:
 * `role="tablist"` e `role="tab"` sem `aria-controls`, sem `role="tabpanel"` e
 * sem navegação por seta. Para quem enxerga, funcionava; para quem usa leitor
 * de tela, era um `tab` que não controla nada — o papel promete uma relação que
 * o DOM não tinha. E o `axe` não reprova isso: `aria-controls` é recomendação,
 * não regra automatizável, o que faz deste o tipo de defeito que sobrevive ao
 * CI verde.
 *
 * Aqui a relação existe nos dois sentidos, o foco anda com as setas (o que a
 * APG exige de um `tablist` de verdade) e a contagem por seção diz **quanto
 * falta** — a informação que o dono pediu para saber onde está.
 */

export type Section = {
  id: string;
  label: string;
  /** Quantos itens a seção guarda. Some quando não há o que contar. */
  count?: number;
};

type SectionBarProps = {
  sections: readonly Section[];
  active: string;
  onSelect: (id: string) => void;
  /** Nomeia a barra. `tablist` sem nome é uma lista sem assunto. */
  label: string;
  /** Prefixo dos `id` — precisa ser único na página. */
  idPrefix: string;
  className?: string;
};

function sectionTabId(idPrefix: string, id: string) {
  return `${idPrefix}-tab-${id}`;
}

function sectionPanelId(idPrefix: string, id: string) {
  return `${idPrefix}-panel-${id}`;
}

export function SectionBar({
  sections,
  active,
  onSelect,
  label,
  idPrefix,
  className,
}: SectionBarProps) {
  const listaRef = useRef<HTMLDivElement>(null);

  // Setas movem o foco E a seleção, que é o comportamento de `tablist` com
  // ativação automática — o certo quando trocar de aba não custa nada (aqui
  // nada é buscado do servidor, só troca o que já está em memória).
  function aoTeclar(evento: KeyboardEvent<HTMLButtonElement>) {
    const teclas = ["ArrowRight", "ArrowLeft", "Home", "End"];
    if (!teclas.includes(evento.key)) return;
    evento.preventDefault();

    const indiceAtual = sections.findIndex((secao) => secao.id === active);
    const ultimo = sections.length - 1;
    const proximo =
      evento.key === "Home"
        ? 0
        : evento.key === "End"
          ? ultimo
          : evento.key === "ArrowRight"
            ? (indiceAtual + 1) % sections.length
            : (indiceAtual - 1 + sections.length) % sections.length;

    const alvo = sections[proximo];
    if (!alvo) return;
    onSelect(alvo.id);
    listaRef.current
      ?.querySelector<HTMLButtonElement>(`#${CSS.escape(sectionTabId(idPrefix, alvo.id))}`)
      ?.focus();
  }

  return (
    <div
      ref={listaRef}
      role="tablist"
      aria-label={label}
      // O `onKeyDown` fica em cada aba, e não aqui: a APG diz que o `tablist`
      // NÃO é focável (quem recebe foco são as abas), e um ouvinte de teclado
      // num elemento não focável é a definição de código morto — o
      // `jsx-a11y/interactive-supports-focus` reclama com razão. Pôr
      // `tabIndex={-1}` só para calar o lint criaria um alvo de foco que a
      // especificação não quer.
      className={cn(
        // TRILHO, e não uma fileira de texto solta.
        //
        // A primeira versão desenhava a aba inativa como texto esmaecido sem
        // borda nem fundo, e o dono apontou em 05/08: *"os botões de abas ficam
        // muito apagados e não dá pra perceber quase que são botões de abas"*.
        // Ele tem razão, e o defeito é de affordance: sem moldura, nada na tela
        // dizia que aquilo era um conjunto de escolhas — parecia legenda.
        //
        // O trilho resolve na forma, não na cor: um contorno em volta do grupo
        // já comunica "escolha uma destas" antes de qualquer leitura, e é por
        // isso que o controle segmentado é o padrão dessa função. A aba inativa
        // também deixou de ser `--muted`: texto de opção clicável é texto de
        // leitura, não legenda.
        // `w-fit` + `justify-self-start`: o trilho abraça as abas em vez de
        // esticar na largura do painel. Esticado ele lê como barra de
        // ferramentas — que é outra coisa —, e o vazio à direita desfaz a
        // própria pista que o trilho existe para dar.
        // TUT-511 — o trilho é o alternador em pílula do Lumen (P34), o mesmo
        // do `AlternadorEmPilula` e do `GrupoDeEscolha` pilula: a receita mora
        // em `superficies.ts`. O `w-fit` e o `justify-self-start` da receita
        // são os do parágrafo acima.
        alternadorEmPilulaClass,
        "justify-self-start",
        className,
      )}
    >
      {sections.map((secao) => {
        const ativa = secao.id === active;
        return (
          <button
            key={secao.id}
            id={sectionTabId(idPrefix, secao.id)}
            type="button"
            role="tab"
            aria-selected={ativa}
            aria-controls={sectionPanelId(idPrefix, secao.id)}
            // Roving tabindex: o Tab entra na barra UMA vez e cai na aba atual;
            // dentro dela quem navega são as setas. Sem isso, uma barra de seis
            // seções custa seis Tabs para chegar ao conteúdo.
            tabIndex={ativa ? 0 : -1}
            onClick={() => onSelect(secao.id)}
            onKeyDown={aoTeclar}
            data-console-section={secao.id}
            // TUT-511 — a opção é a do alternador em pílula (a receita de
            // `superficies.ts`), com `aria-selected` marcando a escolhida. O
            // `font-semibold` nos dois estados e o token compacto (TUT-282:
            // navegação SECUNDÁRIA de painel) vêm junto com ela.
            className={opcaoDoAlternadorClass}
          >
            {secao.label}
            {secao.count === undefined ? null : (
              <>
                {/* TUT-238 — este espaço é o conserto, e ele não é visual.
                    O `gap` do flex sempre desenhou a separação na tela, mas o
                    NOME ACESSÍVEL é a concatenação do conteúdo: sem um nó de
                    texto aqui, o leitor de tela anunciava "Administradora3",
                    uma palavra só. O `axe` não reprova — o nome existe, é único
                    e não está vazio; ele só está errado —, e por isso o defeito
                    atravessou a TUT-158 e os três painéis do Console.

                    Ele fica DENTRO da condição de propósito: a seção sem
                    contagem não pode ganhar espaço sobrando no nome. */}
                {" "}
                <span
                  className={cn(
                    "rounded-full px-[0.4rem] text-legenda font-medium tabular-nums",
                    // Pares medidos: `--primary` sobre `--surface`, `--muted`
                    // sobre `--surface-2`. Preencher a contagem com `--primary`
                    // e escrever em claro reprovaria no tema escuro.
                    ativa
                      ? "bg-[var(--surface)] text-[var(--primary)]"
                      : "bg-[var(--surface-2)] text-[var(--muted)]",
                  )}
                >
                  {secao.count}
                </span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

type SectionPanelProps = {
  id: string;
  idPrefix: string;
  active: boolean;
  children: ReactNode;
  className?: string;
};

/**
 * O outro lado da relação. Fica no DOM só quando é a seção ativa — dentro de um
 * painel isso é legítimo, porque o painel inteiro continua presente e visível
 * para a auditoria; o que alterna é o conteúdo dele, como qualquer aba.
 */
export function SectionPanel({
  id,
  idPrefix,
  active,
  children,
  className,
}: SectionPanelProps) {
  if (!active) return null;
  return (
    <div
      role="tabpanel"
      id={sectionPanelId(idPrefix, id)}
      aria-labelledby={sectionTabId(idPrefix, id)}
      // A APG pede painel focável quando ele não tem controle focável logo no
      // começo; aqui quase sempre tem, mas o custo de garantir é um atributo.
      tabIndex={0}
      className={cn("min-w-0 outline-0", className)}
    >
      {children}
    </div>
  );
}
